import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';

interface CatalogGroup {
  total: number;
  items: Array<{ value: string; count: number }>;
}

interface CatalogCheckResult {
  ok: boolean;
  needRelogin?: boolean;
  reloginReason?: string;
  checkedAt?: string;
  vmRoomTypeCount?: number;
  vmRatePlanCount?: number;
  missingRoomTypes?: Array<{ value: string; count: number }>;
  missingRatePlans?: Array<{ value: string; count: number }>;
  notes?: string[];
}

@Component({
  selector: 'app-catalog',
  imports: [FormsModule],
  template: `
    <section class="stack">
      <header class="head">
        <div>
          <h1>Catalog</h1>
          <p>Unique values from loaded reservations.</p>
          @if (hotelCode()) {
            <div class="property-pill">Property {{ hotelCode() }}@if (hotelName()) { <span>· {{ hotelName() }}</span> }</div>
          }
        </div>
        <div class="head-actions">
          <button type="button" (click)="checkCatalog()" [disabled]="checking()">{{ checking() ? 'Checking…' : 'Check Catalog' }}</button>
        </div>
      </header>

      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      @if (checkResult()) {
        <section class="card check-result">
          <div class="section-head">
            <h2>VM Catalog Check</h2>
            <div class="meta">VM Room Types: {{ checkResult()!.vmRoomTypeCount ?? 0 }} · VM Rate Plans: {{ checkResult()!.vmRatePlanCount ?? 0 }}</div>
          </div>
          <div class="grid">
            <section>
              <h3>Missing Room Types ({{ checkResult()!.missingRoomTypes?.length ?? 0 }})</h3>
              @if ((checkResult()!.missingRoomTypes?.length ?? 0) > 0) {
                <p class="warn">Any reservations using these room types will fail to send.</p>
                <ul class="issues">
                  @for (item of checkResult()!.missingRoomTypes ?? []; track item.value) {
                    <li><code>{{ item.value }}</code> · {{ item.count }} reservation(s)</li>
                  }
                </ul>
              } @else {
                <p class="ok">No room-type mismatches found.</p>
              }
            </section>
            <section>
              <h3>Missing Rate Plans ({{ checkResult()!.missingRatePlans?.length ?? 0 }})</h3>
              @if ((checkResult()!.missingRatePlans?.length ?? 0) > 0) {
                <p class="warn">Reservations with these rate plans will default to RACK.</p>
                <ul class="issues">
                  @for (item of checkResult()!.missingRatePlans ?? []; track item.value) {
                    <li><code>{{ item.value }}</code> · {{ item.count }} reservation(s)</li>
                  }
                </ul>
              } @else {
                <p class="ok">No rate-plan mismatches found.</p>
              }
            </section>
          </div>
        </section>
      }

      <div class="grid">
        <section class="card">
          <div class="section-head">
            <h2>Room Types <span>{{ roomTypes().total }}</span></h2>
            <div class="search">
              <input [(ngModel)]="roomTypeSearch" placeholder="Search room types…">
              <button type="button" class="secondary" (click)="load()">Apply</button>
              <button type="button" class="secondary" (click)="exportCsv('roomtypes')">Export CSV</button>
            </div>
          </div>
          <table>
            <thead><tr><th>Room Type</th><th>Count</th></tr></thead>
            <tbody>
              @for (item of roomTypes().items; track item.value) {
                <tr><td>{{ item.value }}</td><td class="mono">{{ item.count }}</td></tr>
              } @empty {
                <tr><td colspan="2">No room types found.</td></tr>
              }
            </tbody>
          </table>
        </section>

        <section class="card">
          <div class="section-head">
            <h2>Rate Plans <span>{{ ratePlans().total }}</span></h2>
            <div class="search">
              <input [(ngModel)]="ratePlanSearch" placeholder="Search rate plans…">
              <button type="button" class="secondary" (click)="load()">Apply</button>
              <button type="button" class="secondary" (click)="exportCsv('rateplans')">Export CSV</button>
            </div>
          </div>
          <table>
            <thead><tr><th>Rate Plan</th><th>Count</th></tr></thead>
            <tbody>
              @for (item of ratePlans().items; track item.value) {
                <tr><td>{{ item.value }}</td><td class="mono">{{ item.count }}</td></tr>
              } @empty {
                <tr><td colspan="2">No rate plans found.</td></tr>
              }
            </tbody>
          </table>
        </section>
      </div>
    </section>
  `,
  styles: `
    .stack{display:grid;gap:16px}
    .head{display:flex;justify-content:space-between;gap:16px;align-items:end;flex-wrap:wrap}
    .head-actions{display:flex;gap:8px}
    h1,h2{margin:0}
    h3{margin:0 0 6px}
    p{margin:4px 0 0;color:var(--muted)}
    .meta{font-size:12px;color:var(--muted)}
    .msg{padding:8px 10px;border-radius:8px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
    .check-result{gap:14px}
    .issues{margin:8px 0 0;padding-left:18px}
    .issues li{margin:4px 0}
    .warn{color:#8a4b00}
    .ok{color:#245b31}
    .search{display:flex;gap:8px;flex-wrap:wrap}
    .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
    .card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:16px;display:grid;gap:12px}
    .section-head{display:flex;justify-content:space-between;align-items:center}
    span{display:inline-flex;min-width:28px;justify-content:center;padding:2px 8px;border-radius:999px;background:#edf3ff;color:#13356f;font-size:12px}
    input{height:40px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    button{height:40px;border:0;border-radius:10px;background:var(--primary);color:#fff;padding:0 14px;cursor:pointer}
    .secondary{background:#6b778c}
    .property-pill{margin-top:8px;font-size:12px;color:#24416a;background:#edf3ff;border-radius:999px;display:inline-flex;padding:6px 10px;gap:6px}
    .property-pill span{color:#49668e}
    table{width:100%;border-collapse:collapse}
    th,td{padding:10px;border-bottom:1px solid #edf0f6;text-align:left}
    .mono{font-variant-numeric:tabular-nums}
    @media (max-width: 900px){ .grid{grid-template-columns:1fr} }
  `,
})
export class CatalogComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly roomTypes = signal<CatalogGroup>({ total: 0, items: [] });
  readonly ratePlans = signal<CatalogGroup>({ total: 0, items: [] });
  readonly hotelCode = signal('');
  readonly hotelName = signal('');
  readonly checking = signal(false);
  readonly message = signal('');
  readonly isError = signal(false);
  readonly checkResult = signal<CatalogCheckResult | null>(null);
  roomTypeSearch = '';
  ratePlanSearch = '';

  async ngOnInit(): Promise<void> {
    try {
      const ctx = await firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string } | null }>('/context/property'));
      this.hotelCode.set(ctx.activeProperty?.code || '');
      this.hotelName.set(ctx.activeProperty?.name || '');
    } catch {
      this.hotelCode.set('');
      this.hotelName.set('');
    }
    await this.load();
    if (this.route.snapshot.queryParamMap.get('autocheck') === '1') {
      await this.checkCatalog();
      await this.router.navigate([], { relativeTo: this.route, queryParams: { autocheck: null }, queryParamsHandling: 'merge' });
    }
  }

  async load(): Promise<void> {
    const [roomTypes, ratePlans] = await Promise.all([
      firstValueFrom(this.api.get<{ roomTypes: CatalogGroup }>(`/catalog?q=${encodeURIComponent(this.roomTypeSearch.trim())}`)),
      firstValueFrom(this.api.get<{ ratePlans: CatalogGroup }>(`/catalog?q=${encodeURIComponent(this.ratePlanSearch.trim())}`)),
    ]);
    this.roomTypes.set(roomTypes.roomTypes);
    this.ratePlans.set(ratePlans.ratePlans);
  }

  exportCsv(type: 'roomtypes' | 'rateplans'): void {
    window.open(`/api/catalog/export?type=${type}`, '_blank');
  }

  async checkCatalog(): Promise<void> {
    if (this.checking()) return;
    this.checking.set(true);
    this.message.set('');
    this.isError.set(false);
    this.checkResult.set(null);
    try {
      const result = await firstValueFrom(this.api.post<CatalogCheckResult>('/catalog/check', {}));
      if (result.needRelogin) {
        await this.router.navigateByUrl('/import/vm-auth?next=catalog&autocheck=1');
        return;
      }
      if (!result.ok) {
        this.isError.set(true);
        this.message.set('Catalog check failed.');
        return;
      }
      this.checkResult.set(result);
      const missingRoomCount = result.missingRoomTypes?.length ?? 0;
      const missingRateCount = result.missingRatePlans?.length ?? 0;
      this.message.set(`Catalog check complete. Missing room types: ${missingRoomCount}. Missing rate plans: ${missingRateCount}.`);
    } catch {
      this.isError.set(true);
      this.message.set('Catalog check failed.');
    } finally {
      this.checking.set(false);
    }
  }
}
